import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { ContactsUpgradeService } from '../../Contact/contacts.service.upgrade';
import { ProgressIndicatorConfig, ProgressActions, ProgressIndicator } from '../Models/progressIndicator.model';
import { ToastrService } from 'ngx-toastr';
import { of, Observable, lastValueFrom } from 'rxjs';
import { delay } from 'rxjs/operators';
import { DownloadFileService } from '../../Common/Download/downloadFileService';
import { UpgradeNavigationServiceHandler } from '../../Common/Routing/upgrade-navigation-handler.service';
import {
    ReturnAssetRepository,
    AssetRepository,
    ReturnRepository,
    GLAccountRepository,
    EntityImportRepository,
    FormSetupRepository,
    AssessorCommandCenterRepository,
    FilingBatchRepository,
    ReturnFormRepository,
    LongRunningProcessRepository,
    ReturnReportRepository,
    AllocationRepository,
    FormRepository,
    ElectronicFilingRepository,
    ErrorLogRepository,
} from '../../Compliance/Repositories';
import { TaxRateCommandCenterService } from '../../Tax-Rate/Command-Center/tax.rate.command.center.service';
import { ReportProcessIntegrationRepository } from '../../Reporting/ReportProcessIntegrationRepository';
import { AppealApplicationService } from '../../Appeal-Application/appeal.application.service';
import { ForecastingBudgetingService } from '../../Budget/forecasting.budgeting.service';
import { AccrualsPageService } from '../../Accruals/accruals.page.service';
import { AdvancedSearchIntegrationRepository } from '../../Search/AdvancedSearchIntegrationRepository';
import { ActionViewIntegrationRepository } from '../../Task/ActionView/ActionViewIntegrationRepository';
import { AllocationDetailRepository } from '../../Compliance/Repositories/allocationDetail.repository';
import { RentRollRepository } from '../../Valuation/Rent-Roll/rentRoll.repository';
import { IncomeStatementRepository } from '../../Valuation/Income-Statement/incomeStatement.repository';
import { CsrCommandCenterRepository } from '../../Client-Services/Command-Center/csrCommandCenter.repository';
import { FormLibraryRepository } from '../../Compliance/Repositories/formLibraryRepository';
import { AppealRecommendationRepository } from '../../Appeal-Recommendation/appealRecommendation.repository';
import { PaymentBatchRepository } from 'src/app/Core-Repositories/paymentBatch.repository';
import { ResponsibleEntityRepository } from '../../Responsible-Entity/responsbileEntity.repository';
import { TransmittalRepository } from '../../Core-Repositories';
import { GetLogFilesRepository } from '../../Diagnostics/Get-Log-Files/getLogFiles.repository';
import { AttachmentBundlerRepository } from '../../Attachment/Bundler/attachmentBundler.repository';
import { AttachmentService } from '../../Attachment/attachment.service';

type BaseConfig = (p: Compliance.LongRunningProcessModel) => ProgressIndicatorConfig;

export interface ProcessMessages {
    title: string;
    downloaded?: string;
    cancelled?: string;
    complete?: string;
}

@Injectable()
export class LongRunningProcessIndicatorConfigService {
    constructor(
        private readonly _accrualsPageService: AccrualsPageService,
        private readonly _actionViewIntegrationRepository: ActionViewIntegrationRepository,
        private readonly _allocationDetailRepository: AllocationDetailRepository,
        private readonly _allocationRepository: AllocationRepository,
        private readonly _appealApplicationService: AppealApplicationService,
        private readonly _appealRecommendationRepository: AppealRecommendationRepository,
        private readonly _assetRepository: AssetRepository,
        private readonly _assessorCommandCenterRepository: AssessorCommandCenterRepository,
        private readonly _attachmentBundlerRepository: AttachmentBundlerRepository,
        private readonly _attachmentService: AttachmentService,
        private readonly _avancedSearchIntegrationRepository: AdvancedSearchIntegrationRepository,
        private readonly _contactService: ContactsUpgradeService,
        private readonly _csrCommandCenterRepository: CsrCommandCenterRepository,
        private readonly _downloadFileService: DownloadFileService,
        private readonly _electronicFilingRepository: ElectronicFilingRepository,
        private readonly _entityImportRepository: EntityImportRepository,
        private readonly _errorLogRepository: ErrorLogRepository,
        private readonly _filingBatchRepository: FilingBatchRepository,
        private readonly _forecastingBudgetingService: ForecastingBudgetingService,
        private readonly _formLibraryRepository: FormLibraryRepository,
        private readonly _formRepository: FormRepository,
        private readonly _formSetupRepository: FormSetupRepository,
        private readonly _getLogFilesRepository: GetLogFilesRepository,
        private readonly _glAccountRepository: GLAccountRepository,
        private readonly _incomeStatementLineRepository: IncomeStatementRepository,
        private readonly _longRunningProcessRepository: LongRunningProcessRepository,
        private readonly _paymentBatchRepository: PaymentBatchRepository,
        private readonly _rentRollRepository: RentRollRepository,
        private readonly _reportProcessIntegrationRepository: ReportProcessIntegrationRepository,
        private readonly _responsibleEntityRepository: ResponsibleEntityRepository,
        private readonly _returnAssetRepository: ReturnAssetRepository,
        private readonly _returnFormRepository: ReturnFormRepository,
        private readonly _returnReportRepository: ReturnReportRepository,
        private readonly _returnRepository: ReturnRepository,
        private readonly _routerService: UpgradeNavigationServiceHandler,
        private readonly _taxRateCommandCenterService: TaxRateCommandCenterService,
        private readonly _toastsManager: ToastrService,
        private readonly _transmittalRepository: TransmittalRepository,
    ) {}

    get processMap(): Map<Compliance.LongRunningProcessTypeEnum, BaseConfig> {
        return this._processMap;
    }

    private _processMap: Map<Compliance.LongRunningProcessTypeEnum, BaseConfig>;

    /**
     * Base configuration settings
     * These are the process specific settings for the generic base configurations
     */

    private _commonMethods = {
        error: (status) => this._toastsManager.error(status),
        cancel: async (indicatorRef) => {
            await lastValueFrom(this._longRunningProcessRepository.cancel(indicatorRef.longRunningProcessId));
        },
        cancelled: (message: string) => () => this._toastsManager.error(message),
        acknowledge: async (lrpId: number) => {
            await this._longRunningProcessRepository.acknowledge(lrpId);
        },
        complete: (snack: ProgressIndicator, message: string) => {
            if (message) {
                snack.updateTitle(message);
                snack.updateMessage(null);
            }
        },
    };

    private _importOperations = new Map([
        ['Process', { title: 'Importing File', cancelled: 'File import cancelled', complete: 'File import complete' }],
        [
            'Transfer',
            {
                title: 'Transferring',
                cancelled: 'File transfer cancelled',
                complete: 'File transfer complete',
            },
        ],
        ['Revert', { title: 'Reverting Import', cancelled: 'File revert cancelled', complete: 'File revert complete' }],
        [
            'Validate',
            {
                title: 'Validating Import',
                cancelled: 'File validate cancelled',
                complete: 'File validate complete',
            },
        ],
        [
            'Bulk Update',
            {
                title: 'Bulk Updating Import',
                cancelled: 'Bulk update cancelled',
                complete: 'Bulk update complete',
            },
        ],
        ['Update Field', { title: 'Updating Import', cancelled: 'Update cancelled', complete: 'Update complete' }],
        [
            'Recalculate',
            {
                title: 'Recalculating Assets',
                cancelled: 'Asset recalculation cancelled',
                complete: 'Asset recalculation complete',
            },
        ],
    ]);

    private _processMessageMap: Map<
        Compliance.LongRunningProcessTypeEnum,
        (process?: Compliance.LongRunningProcessModel) => ProcessMessages
    > = new Map([
        [
            Compliance.LongRunningProcessTypeEnum.AccrualsGridGet,
            () => ({
                title: 'Retrieving Accruals Grid',
                complete: 'Accruals Grid Retrieved',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ActionViewExport,
            () => ({
                title: 'Exporting search results',
                downloaded: 'Downloading',
                cancelled: 'Search results export cancelled',
                complete: 'Export complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AddMissingForecastTaxYear,
            () => ({
                title: 'Creating Tax Years',
                cancelled: 'Add forecast tax year cancelled',
                complete: 'Creating tax years complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AddTaxYearAndAnnualBudget,
            () => ({
                title: 'Creating Tax Years/Budgets',
                cancelled: 'Add tax year and annual budget cancelled',
                complete: 'Creating tax years/budgets complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AddYearToCompanyPortfolio,
            () => ({
                title: 'Adding Year',
                cancelled: 'Adding year cancelled',
                complete: 'Adding year complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AppealListReport,
            () => ({
                title: 'Retrieving Appeal List Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ARBValueReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ARBCoverLetterReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AssetBulkDelete,
            () => ({
                title: 'Deleting assets',
                cancelled: 'Deleting assets cancelled',
                complete: 'Deleting assets complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AssetBulkUpdate,
            () => ({
                title: 'Updating assets',
                cancelled: 'Updating assets cancelled',
                complete: 'Updating assets complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.BudgetReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.BudgetVarianceReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.CloseAccrualAccountingPeriod,
            () => ({
                title: 'Closing Accrual Accounting Period',
                complete: 'Close Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.CopyMoveEntity,
            () => ({
                title: 'Copy/Move Site/Parcel',
                complete: 'Site/Parcel copied/moved',
                cancelled: 'Copy/Move Site/Parcel cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.DataBackfillNonRatesTaxBillRecalc,
            () => ({
                title: 'Backfill Non-Rates Tax Bill Recalc',
                complete: 'Non-Rates Tax Bill Recalc Completed',
                cancelled: 'Non-Rates Tax Bill Recalc Cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.DataBackfillSiteAddressLatLongUpdate,
            () => ({
                title: 'Backfill Site Address Lat/Long Update',
                complete: 'Update for Site Address Lat/Long Completed',
                cancelled: 'Update for Site Address Lat/Long Cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.DataBackfillInactiveEntityCsrToNa,
            () => ({
                title: 'Backfill Inactive Entity CSRs to N/A',
                complete: 'Inactive Entity CSR Backfill Completed',
                cancelled: 'Inactive Entity CSR Backfill Cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.DataBackfillTaxableValues,
            () => ({
                title: 'Backfill Taxable Values',
                complete: 'Taxable Values Backfill Completed',
                cancelled: 'Taxable Values Backfill Cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.DataBackfillTrueRateAppealSavingsRecalc,
            () => ({
                title: 'Backfill Appeal Savings True Rate Recalc',
                complete: 'Appeal Savings Recalc for True Rate Completed',
                cancelled: 'Appeal Savings Recalc for True Rate Cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.DeleteTaxAuthorityTaxRateAreaUpdateOtherEntities,
            () => ({
                title: 'Deleting Tax Authority / Tax Rate Area ',
                complete: 'Deleting Tax Authority / Tax Rate Area Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.DownloadLogFile,
            () => ({
                title: 'Download Log File',
                downloaded: 'Downloading',
                complete: 'Download Log File Completed',
                cancelled: 'Download Log File Cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ErrorLogExport,
            () => ({
                title: 'Exporting Error Log Entries',
                downloaded: 'Downloading',
                cancelled: 'Error Log Entries export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAccrualDataToFile,
            () => ({
                title: 'Exporting Accrual Data to File',
                complete: 'Export Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAccrualsGridToExcel,
            () => ({
                title: 'Exporting Rows',
                downloaded: 'Downloading',
                cancelled: 'Accruals export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAllocationDetails,
            () => ({
                title: 'Exporting Allocation Details',
                downloaded: 'Downloading',
                cancelled: 'Allocation Details export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAllocations,
            () => ({
                title: 'Exporting Allocations',
                downloaded: 'Downloading',
                cancelled: 'Allocations export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAppealRecommendationCommandCenter,
            () => ({
                title: 'Exporting',
                downloaded: 'Downloading',
                cancelled: 'Export cancelled',
                complete: 'Export Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAssessorCommandCenterAssessors,
            () => ({
                title: 'Exporting Rows',
                downloaded: 'Downloading',
                cancelled: 'State & Jurisdiction export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportBudgetGridToExcel,
            () => ({
                title: 'Exporting Rows',
                downloaded: 'Downloading',
                cancelled: 'Forecasting Budgeting export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportClientServiceResponsibilityCommandCenter,
            () => ({
                title: 'Exporting CSRs',
                complete: 'Export complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportContacts,
            () => ({
                title: 'Exporting Contacts',
                downloaded: 'Downloading',
                cancelled: 'Export cancelled',
                complete: 'Export Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportElectronicFiling,
            () => ({
                title: 'Exporting Electronic Filings',
                downloaded: 'Downloading',
                cancelled: 'Export cancelled',
                complete: 'Export Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportFormList,
            () => ({
                title: 'Exporting Form List',
                downloaded: 'Downloading',
                cancelled: 'Form list export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportGLAccounts,
            () => ({
                title: 'Exporting G/L Accounts',
                downloaded: 'Downloading',
                cancelled: 'Exporting G/L Accounts cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportImportStagingRows,
            () => ({
                title: 'Exporting Rows',
                downloaded: 'Downloading',
                cancelled: 'Exporting rows cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportLRPList,
            () => ({
                title: 'Exporting Long Running Process List',
                downloaded: 'Downloading',
                cancelled: 'Long Running Process List export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportReportingAssets,
            () => ({
                title: 'Exporting Assets',
                downloaded: 'Downloading',
                cancelled: 'Asset export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportResponsibleEntities,
            () => ({
                title: 'Exporting Responsible Entities',
                downloaded: 'Downloading',
                cancelled: 'Responsible Entities export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportReturnAssets,
            () => ({
                title: 'Exporting Assets',
                downloaded: 'Downloading',
                cancelled: 'Asset export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportReturnFilingControlSummary,
            () => ({
                title: 'Generating Filing Control for Tracker report',
                downloaded: 'Downloading',
                cancelled: 'Generating Filing Control for Tracker report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportReturnReconciliation,
            () => ({
                title: 'Exporting Report',
                downloaded: 'Downloading',
                cancelled: 'Reconciliation Report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportSourceAssets,
            () => ({
                title: 'Exporting Assets',
                downloaded: 'Downloading',
                cancelled: 'Asset export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportTaxRates,
            () => ({
                title: 'Exporting Tax Rates',
                downloaded: 'Downloading',
                cancelled: 'Exporting tax rates cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FactorTableExportAttachments,
            () => ({
                title: 'Exporting Factor Tables Attachments',
                downloaded: 'Downloading',
                cancelled: 'Factor Tables Attachments export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FinalizeTransmittal,
            () => ({
                title: 'Finalizing Payment Package',
                complete: 'Payment Package Finalized',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FinalValueReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FormClassificationMappingExport,
            () => ({
                title: 'Exporting Asset Class Mappings',
                downloaded: 'Downloading',
                cancelled: 'Asset Class Mappings export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FormLibraryExport,
            () => ({
                title: 'Exporting Form Library',
                downloaded: 'Downloading',
                cancelled: 'Form library export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FormSetupExport,
            () => ({
                title: 'Exporting Forms Setup',
                downloaded: 'Downloading',
                cancelled: 'Exporting form setup cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FormSetupImport,
            () => ({
                title: 'Importing Forms Setup',
                cancelled: 'Importing form setup cancelled',
                complete: 'Importing forms setup complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GenerateElectronicFilingOutput,
            () => ({
                title: 'Electronic Filing Output',
                downloaded: 'Downloading',
                cancelled: 'Electronic filing output download cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GenerateReturnOutput,
            () => ({
                title: 'Return Output',
                downloaded: 'Downloading',
                cancelled: 'Return output download cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GeocodeSiteAddressesPostImport,
            () => ({
                title: 'Geocoding Site Addresses Post-Import',
                cancelled: 'Geocoding Site Addresses cancelled',
                complete: 'Geocoding Site Addresses Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GetAppealApplications,
            () => ({
                title: 'Retrieving Appeal Applications',
                cancelled: 'Retrieving appeal applications cancelled',
                complete: 'Retrieving appeal applications complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GetAppealApplicationsDownload,
            () => ({
                title: 'Downloading Appeal Applications',
                downloaded: 'Downloading',
                cancelled: 'Download cancelled',
                complete: 'Complete',
            }),
        ],
        //  [Compliance.LongRunningProcessTypeEnum.GetLogFileList, (process) => ({
        //   title: 'Get Log File List',
        // complete: 'Get Log File List Completed',
        // cancelled: 'Get Log File List Cancelled'
        //  })],
        [
            Compliance.LongRunningProcessTypeEnum.ImportFile,
            (process?) => ({
                title: this._importOperations.get(process.operationName).title,
                cancelled: this._importOperations.get(process.operationName).cancelled,
                complete: this._importOperations.get(process.operationName).complete,
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.IncomeStatementLine,
            () => ({
                title: 'Exporting Income Statement',
                complete: 'Export complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.IncomeStatementLineBulkDelete,
            () => ({
                title: 'Delete Income Statement',
                complete: 'Deleting Income Statement complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.InitialValueReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.MaarReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchCheckImport,
            () => ({
                title: 'Importing Checks to Payment Batch',
                complete: 'Importing Checks to Payment Batch Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchCompleteTask,
            () => ({
                title: 'Payment Batch Complete Task',
                complete: 'Payment Batch Create Completed',
                cancelled: 'Payment Batch Create Cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchDetailsExport,
            () => ({
                title: 'Exporting Payment Batch',
                downloaded: 'Downloading',
                cancelled: 'Payment Batch export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchDetailsPaymentBulkDelete,
            () => ({
                title: 'Removing Batch Payments',
                complete: 'Removing Batch Payments Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchExportFeedsAndFiles,
            () => ({
                title: 'Downloading Payment Batch Feeds and Files',
                downloaded: 'Downloading',
                complete: 'Downloaded Payment Batch Feeds and Files',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchExportList,
            () => ({
                title: 'Exporting Payment Batches',
                downloaded: 'Downloading',
                cancelled: 'Payment Batch export cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchRescindTask,
            () => ({
                title: 'Payment Batch Rescind Task',
                complete: 'Payment Batch Rescind Completed',
                cancelled: 'Payment Batch Rescind Cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PseReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PsrReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        // Replaced by UpdateEntitiesForTaxAuthorityOrTaxRateAreaChange and DeleteTaxAuthorityTaxRateAreaUpdateOtherEntities
        // [
        //     Compliance.LongRunningProcessTypeEnum.RecalculateTaxBillsForTaChange,
        //     (process) => ({
        //         title: 'Recalculate Tax Bills',
        //         complete: 'Recalculation complete',
        //     }),
        // ],
        [
            Compliance.LongRunningProcessTypeEnum.RentRollLineItem,
            () => ({
                title: 'Exporting Rent Roll',
                complete: 'Export complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.RentRollLineItemBulkDelete,
            () => ({
                title: 'Delete Rent Roll Line Item',
                complete: 'Deleting Rent Roll Line Item complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ReopenAccrualAccountingPeriod,
            () => ({
                title: 'Re-opening Accrual Accounting Period',
                complete: 'Re-open Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.Return,
            () => ({
                title: 'Processing',
                cancelled: 'Return process cancelled',
                complete: 'Return process complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ReturnFormDownload,
            () => ({
                title: 'Return Form Download',
                downloaded: 'Downloading',
                cancelled: 'Return form download cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ReturnPrepareData,
            () => ({
                title: 'Preparing Filing Batch Data',
                complete: 'Filing Batch Is Ready',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SiteParcelDelete,
            () => ({
                title: 'Deleting Site/Parcel',
                complete: 'Site/Parcel deleted',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SiteReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SmartSearchExport,
            () => ({
                title: 'Exporting search results',
                downloaded: 'Downloading',
                cancelled: 'Search results export cancelled',
                complete: 'Export complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SupportInstanceEscalationActivityReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SyncBudgets,
            () => ({
                title: 'Syncing Parcel Budgets',
                cancelled: 'Syncing parcel budgets cancelled',
                complete: 'Syncing parcel budgets complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.Synthetic,
            () => ({
                title: 'Testing',
                cancelled: 'Test process cancelled',
                complete: 'Test process complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.TransmitAccrual,
            () => ({
                title: 'Transmitting Accrual Information',
                complete: 'Accrual transmission complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UpdateCsr,
            () => ({
                title: 'Updating CSR',
                complete: 'CSR updated',
                cancelled: 'CSR Update cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UpdateCsrBulk,
            () => ({
                title: 'Updating CSRs',
                complete: 'CSRs updated',
                cancelled: 'CSR Updates cancelled',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UpdateTaxAuthority,
            () => ({
                title: 'Update Tax Authority',
                complete: 'Tax Authority Updated',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UserAccessReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UserActivityReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UserCsrReport,
            () => ({
                title: 'Retrieving Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.TaxSavingsReport,
            () => ({
                title: 'Retrieving Tax Savings Report',
                downloaded: 'Downloading',
                cancelled: 'Generating report cancelled',
                complete: 'Complete',
            }),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UserTeamUpdate,
            () => ({
                title: 'User Team Changes',
                complete: 'User Team Changes complete',
            }),
        ],
    ]);

    private _downloadServiceMap: Map<
        Compliance.LongRunningProcessTypeEnum,
        (process: Compliance.LongRunningProcessModel) => Observable<HttpResponse<Blob>>
    > = new Map([
        [
            Compliance.LongRunningProcessTypeEnum.ActionViewExport,
            (process) => this._actionViewIntegrationRepository.downloadSearchResults(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AppealListReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ARBValueReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ARBCoverLetterReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.BudgetReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.BudgetVarianceReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.DownloadLogFile,
            (process) => this._getLogFilesRepository.getDownloadLogFile(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ErrorLogExport,
            (process) => this._errorLogRepository.getExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAccrualsGridToExcel,
            (process) => this._accrualsPageService.getExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAllocationDetails,
            (process) => {
                const params: Compliance.AllocationDetailExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }

                return this._allocationDetailRepository.getExport(params.allocationId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAllocations,
            (process) => {
                const params: Compliance.AllocationExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }

                return this._allocationRepository.getExport(params.companyId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAppealRecommendationCommandCenter,
            (process) => this._appealRecommendationRepository.getExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportAssessorCommandCenterAssessors,
            (process) => this._assessorCommandCenterRepository.getExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportBudgetGridToExcel,
            (process) => this._forecastingBudgetingService.getExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportClientServiceResponsibilityCommandCenter,
            (process) => {
                const params: Core.ClientServiceResponsibilityCommandCenterExportLongRunningProcessModel = JSON.parse(
                    process.parameters,
                );
                if (!params) {
                    return;
                }
                return this._csrCommandCenterRepository.getExport(process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportContacts,
            (process) => this._contactService.getExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportElectronicFiling,
            (process) => this._electronicFilingRepository.getExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportFormList,
            (process) => {
                const params: Compliance.ExportFormListLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._formRepository.getExportFormList(process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportGLAccounts,
            (process) => {
                const params: Compliance.GLAccountExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._glAccountRepository.getExport(params.companyId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportImportStagingRows,
            (process) => this._entityImportRepository.getExport(process.entityId, process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportLRPList,
            (process) => {
                const params: Compliance.ExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._longRunningProcessRepository.getExport(process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportReportingAssets,
            (process) => {
                const params: Compliance.AssetExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._assetRepository.getExport(params.companyId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportResponsibleEntities,
            (process) => {
                const params: Core.ResponsibleEntityExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }

                return this._responsibleEntityRepository.getExport(params.companyId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportReturnAssets,
            (process) =>
                this._returnAssetRepository.getReturnAssetsExport(process.entityId, process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportReturnFilingControlSummary,
            (process) => {
                const params: Compliance.ExportReturnFilingControlSummaryLongRunningProcessModel = JSON.parse(
                    process.parameters,
                );
                if (!params) {
                    return;
                }
                return this._returnRepository.getExportedReturnFilingControlSummary(
                    params.filingBatchId,
                    process.longRunningProcessId,
                );
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportReturnReconciliation,
            (process) => {
                const params: Compliance.ImportFileProcessParametersModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._returnRepository.getReconciliationReport(params.companyId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportSourceAssets,
            (process) => {
                const params: Compliance.AssetExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._assetRepository.getExport(params.companyId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ExportTaxRates,
            (process) => this._taxRateCommandCenterService.getTaxRateDataExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FactorTableExportAttachments,
            (process) => {
                const params: Compliance.FactorTableExportAttachmentLongRunningProcessModel = JSON.parse(
                    process.parameters,
                );
                if (!params) {
                    return;
                }
                return this._formLibraryRepository.getExport(process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FinalizeTransmittal,
            (process) => this._transmittalRepository.GetSimulateTransmitResult(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FinalValueReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FormClassificationMappingExport,
            (process) => {
                const params: Compliance.FormRevisionForLibraryModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._formRepository.getFormClassificationMappingsExport(process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FormLibraryExport,
            (process) => {
                const params: Compliance.FormLibraryExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._formLibraryRepository.getExport(process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FormSetupExport,
            (process) => this._formSetupRepository.getExport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GenerateElectronicFilingOutput,
            (process) => this._electronicFilingRepository.getElectronicFilingOutput(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GenerateReturnOutput,
            (process) => this._returnRepository.getDownloadOutput(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GetAppealApplications,
            (process) => this._appealApplicationService.getOutputFile(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.GetAppealApplicationsDownload,
            (process) => this._appealApplicationService.getDownloadedFile(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.IncomeStatementLine,
            (process) => {
                const params: Compliance.IncomeStatementLineExportLongRunningProcessModel = JSON.parse(
                    process.parameters,
                );
                if (!params) {
                    return;
                }
                return this._incomeStatementLineRepository.getExport(params.companyId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.InitialValueReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.MaarReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchDetailsExport,
            (process) => this._paymentBatchRepository.getFilesAndFeeds(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchExportFeedsAndFiles,
            (process) => this._paymentBatchRepository.getDetailsFilesAndFeeds(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PaymentBatchExportList,
            (process) => this._paymentBatchRepository.getFilesAndFeeds(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PseReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.PsrReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.RentRollLineItem,
            (process) => {
                const params: Compliance.RentRollLineItemExportLongRunningProcessModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                return this._rentRollRepository.getExport(params.companyId, process.longRunningProcessId);
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.ReturnFormDownload,
            (process) => this._returnFormRepository.getDownloadForm(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SiteReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SmartSearchExport,
            (process) => this._avancedSearchIntegrationRepository.downloadSearchResults(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SupportInstanceEscalationActivityReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UserAccessReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UserActivityReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.UserCsrReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
        [
            Compliance.LongRunningProcessTypeEnum.TaxSavingsReport,
            (process) => this._reportProcessIntegrationRepository.downloadReport(process.longRunningProcessId),
        ],
    ]);

    private _navigateToMap: Map<
        Compliance.LongRunningProcessTypeEnum,
        (process?: Compliance.LongRunningProcessModel) => void
    > = new Map([
        [
            Compliance.LongRunningProcessTypeEnum.AddMissingForecastTaxYear,
            (process) => {
                const params: Core.ForecastParcelMissingYearsDTO = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                this._routerService.go('forecastingBudgeting', { companyId: params.topLevelCompanyId });
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AddTaxYearAndAnnualBudget,
            (process) => {
                const params: Core.BudgetMissingTaxYearAndAnnualBudgetRequest = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                this._routerService.go('forecastingBudgeting', { companyId: params.topLevelCompanyId });
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AddYearToCompanyPortfolio,
            (process) => {
                const params: Core.CompanyAddYearDTO = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                this._routerService.go('company', { companyId: params.companyId });
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.AssetBulkUpdate,
            (process) => {
                const params: {
                    companyId: number;
                    model: Compliance.ReportingAssetBulkUpdateModel;
                } = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                this._routerService.go('assetList', { companyId: params.companyId });
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.FinalizeTransmittal,
            (process) => {
                console.log(process);
            },
        ],
        [Compliance.LongRunningProcessTypeEnum.FormSetupImport, () => this._routerService.go('formList', {})],
        [
            Compliance.LongRunningProcessTypeEnum.ImportFile,
            (process) => {
                const params: Compliance.ImportFileProcessParametersModel = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                if (params && params.companyId) {
                    this._routerService.go('processCompanyImport', {
                        companyId: params.companyId,
                        importId: params.importFileId,
                    });
                } else {
                    this._routerService.go('processSystemImport', { importId: params.importFileId });
                }
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.Return,
            (process) => {
                this._routerService.go('returnFilingBatchPage', { filingBatchId: process.entityId });
            },
        ],
        [
            Compliance.LongRunningProcessTypeEnum.SyncBudgets,
            (process) => {
                const params: Core.BudgetMissingTaxYearAndAnnualBudgetRequest = JSON.parse(process.parameters);
                if (!params) {
                    return;
                }
                this._routerService.go('forecastingBudgeting', { companyId: params.topLevelCompanyId });
            },
        ],
        [Compliance.LongRunningProcessTypeEnum.Synthetic, () => this._routerService.go('longRunningProcess', {})],
    ]);

    init(): void {
        // Important!! If you add a new process using one of the base configs you must also define the settings for your new process
        this._processMap = new Map([
            [Compliance.LongRunningProcessTypeEnum.AccrualsGridGet, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.ActionViewExport, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.AddMissingForecastTaxYear, this._baseNavigateCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.AddTaxYearAndAnnualBudget, this._baseNavigateCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.AddYearToCompanyPortfolio, this._baseNavigateCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.AppealListReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.ARBValueReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.ARBCoverLetterReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.AssetBulkDelete, this._baseNavigateConfig],
            [Compliance.LongRunningProcessTypeEnum.AssetBulkUpdate, this._baseNavigateConfig],
            [Compliance.LongRunningProcessTypeEnum.AttachmentBundler, this._attachmentBundlerDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.BudgetReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.BudgetVarianceReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.CloseAccrualAccountingPeriod, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.CopyMoveEntity, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.DataBackfillNonRatesTaxBillRecalc, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.DataBackfillSiteAddressLatLongUpdate, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.DataBackfillInactiveEntityCsrToNa, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.DataBackfillTaxableValues, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.DataBackfillTrueRateAppealSavingsRecalc, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.DeleteTaxAuthorityTaxRateAreaUpdateOtherEntities, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.DownloadLogFile, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.ErrorLogExport, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportAccrualDataToFile, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportAccrualsGridToExcel, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportAllocationDetails, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportAllocations, this._baseDownloadWithCancelConfig],
            [
                Compliance.LongRunningProcessTypeEnum.ExportAppealRecommendationCommandCenter,
                this._baseDownloadWithCancelConfig,
            ],
            [
                Compliance.LongRunningProcessTypeEnum.ExportAssessorCommandCenterAssessors,
                this._baseDownloadWithCancelConfig,
            ],
            [Compliance.LongRunningProcessTypeEnum.ExportBudgetGridToExcel, this._baseDownloadWithCancelConfig],
            [
                Compliance.LongRunningProcessTypeEnum.ExportClientServiceResponsibilityCommandCenter,
                this._baseDownloadConfig,
            ],
            [Compliance.LongRunningProcessTypeEnum.ExportContacts, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportElectronicFiling, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportFormList, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportGLAccounts, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportImportStagingRows, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportLRPList, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportReportingAssets, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportResponsibleEntities, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportReturnAssets, this._baseDownloadWithCancelConfig],
            [
                Compliance.LongRunningProcessTypeEnum.ExportReturnFilingControlSummary,
                this._baseDownloadWithCancelConfig,
            ],
            [Compliance.LongRunningProcessTypeEnum.ExportReturnReconciliation, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportSourceAssets, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ExportTaxRates, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.FactorTableExportAttachments, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.FinalizeTransmittal, this._finalizeTransmittalConfig],
            [Compliance.LongRunningProcessTypeEnum.FinalValueReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.FormClassificationMappingExport, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.FormLibraryExport, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.FormSetupExport, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.FormSetupImport, this._baseNavigateConfig],
            [Compliance.LongRunningProcessTypeEnum.GenerateElectronicFilingOutput, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.GenerateReturnOutput, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.GeocodeSiteAddressesPostImport, null],
            [Compliance.LongRunningProcessTypeEnum.GetAppealApplications, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.GetAppealApplicationsDownload, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ImportFile, this._importFileConfig],
            [Compliance.LongRunningProcessTypeEnum.IncomeStatementLine, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.IncomeStatementLineBulkDelete, this._baseNavigateConfig],
            [Compliance.LongRunningProcessTypeEnum.InitialValueReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.MaarReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.PaymentBatchCheckImport, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.PaymentBatchCompleteTask, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.PaymentBatchDetailsExport, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.PaymentBatchDetailsPaymentBulkDelete, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.PaymentBatchExportFeedsAndFiles, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.PaymentBatchExportList, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.PaymentBatchRescindTask, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.PseReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.PsrReport, this._baseDownloadConfig],
            // Replaced by UpdateEntitiesForTaxAuthorityOrTaxRateAreaChange and DeleteTaxAuthorityTaxRateAreaUpdateOtherEntities
            //[Compliance.LongRunningProcessTypeEnum.RecalculateTaxBillsForTaChange, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.RentRollLineItem, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.RentRollLineItemBulkDelete, this._baseNavigateConfig],
            [Compliance.LongRunningProcessTypeEnum.ReopenAccrualAccountingPeriod, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.Return, this._baseNavigateCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ReturnFormDownload, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.ReturnPrepareData, this._returnPrepareDataConfig],
            [Compliance.LongRunningProcessTypeEnum.ReturnReportDownload, this._returnReportDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.SiteParcelDelete, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.SiteReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.SmartSearchExport, this._baseDownloadWithCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.SupportInstanceEscalationActivityReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.SyncBudgets, this._baseNavigateCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.Synthetic, this._baseNavigateConfig],
            [Compliance.LongRunningProcessTypeEnum.TransmitAccrual, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.UpdateCsr, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.UpdateCsrBulk, this._baseCancelConfig],
            [Compliance.LongRunningProcessTypeEnum.UpdateTaxAuthority, this._baseConfig],
            [Compliance.LongRunningProcessTypeEnum.UserAccessReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.UserActivityReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.UserCsrReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.TaxSavingsReport, this._baseDownloadConfig],
            [Compliance.LongRunningProcessTypeEnum.UserTeamUpdate, this._baseConfig],
            //[Compliance.LongRunningProcessTypeEnum.GetLogFileList, this._baseCancelConfig],
        ]);
    }

    /**
     * Base configurations
     * These are generic configurations that utilize settings maps to define their behavior
     * The unique settings for each process must be defined in the settings maps
     */

    private _baseConfig: BaseConfig = (process) => {
        const messages = this._processMessageMap.get(process.longRunningProcessTypeId)(process);
        return {
            identifier: `LRP_${process.longRunningProcessId}`,
            longRunningProcessId: process.longRunningProcessId,
            title: process.isCompleted ? messages.complete : messages.title,
            progressActions: [],
            isComplete: process.isCompleted,
            progressFailure: !!process.error,
            process: process,
            error: this._commonMethods.error,
            complete: (snack) => this._commonMethods.complete(snack, messages.complete),
            cancelled: this._commonMethods.cancelled(messages.cancelled),
        };
    };

    private _baseDownloadConfig: BaseConfig = (process) => {
        const downloadServiceCall = this._downloadServiceMap.get(process.longRunningProcessTypeId);
        const messages = this._processMessageMap.get(process.longRunningProcessTypeId)(process);

        return {
            identifier: `LRP_${process.longRunningProcessId}`,
            longRunningProcessId: process.longRunningProcessId,
            title: messages.title,
            progressActions: [ProgressActions.Download],
            isComplete: process.isCompleted,
            progressFailure: !!process.error,
            process: process,
            error: this._commonMethods.error,
            complete: (snack) => {
                snack.updateMessage(messages.complete);
            },
            download: async (snack) => {
                snack.updateTitle(messages.downloaded);
                const response = await lastValueFrom(downloadServiceCall(snack.process));
                await this._downloadFileService.downloadResponse(response);
            },
        };
    };

    private _baseDownloadWithCancelConfig: BaseConfig = (process) => {
        const downloadServiceCall = this._downloadServiceMap.get(process.longRunningProcessTypeId);
        const messages = this._processMessageMap.get(process.longRunningProcessTypeId)(process);

        return {
            identifier: `LRP_${process.longRunningProcessId}`,
            longRunningProcessId: process.longRunningProcessId,
            title: messages.title,
            progressActions: [ProgressActions.DownloadWithCancel],
            isComplete: process.isCompleted,
            progressFailure: !!process.error,
            process: process,
            error: this._commonMethods.error,
            cancel: this._commonMethods.cancel,
            cancelled: this._commonMethods.cancelled(messages.cancelled),
            complete: (snack) => {
                snack.updateMessage(messages.complete);
            },
            download: async (snack) => {
                snack.updateTitle(messages.downloaded);
                const response = await lastValueFrom(downloadServiceCall(snack.process));
                await this._downloadFileService.downloadResponse(response);
            },
        };
    };

    private _baseNavigateConfig: BaseConfig = (process) => {
        const messages = this._processMessageMap.get(process.longRunningProcessTypeId)(process);

        return {
            identifier: `LRP_${process.longRunningProcessId}`,
            longRunningProcessId: process.longRunningProcessId,
            title: process.isCompleted ? messages.complete : messages.title,
            progressActions: [ProgressActions.Navigate],
            isComplete: process.isCompleted,
            progressFailure: !!process.error,
            process: process,
            error: this._commonMethods.error,
            complete: (snack) => this._commonMethods.complete(snack, messages.complete),
            navigate: (progress: ProgressIndicator) => {
                const routeTo = this._navigateToMap.get(process.longRunningProcessTypeId);
                routeTo(progress.process);
                progress.remove();
            },
        };
    };

    private _baseNavigateCancelConfig: BaseConfig = (process) => {
        const messages = this._processMessageMap.get(process.longRunningProcessTypeId)(process);

        return {
            identifier: `LRP_${process.longRunningProcessId}`,
            longRunningProcessId: process.longRunningProcessId,
            title: process.isCompleted ? messages.complete : messages.title,
            progressActions: [ProgressActions.Navigate, ProgressActions.Cancel],
            isComplete: process.isCompleted,
            progressFailure: !!process.error,
            process: process,
            error: this._commonMethods.error,
            cancel: this._commonMethods.cancel,
            cancelled: this._commonMethods.cancelled(messages.cancelled),
            complete: (snack) => this._commonMethods.complete(snack, messages.complete),
            navigate: (progress: ProgressIndicator) => {
                const routeTo = this._navigateToMap.get(process.longRunningProcessTypeId);
                routeTo(progress.process);
                progress.remove();
            },
        };
    };

    private _baseCancelConfig: BaseConfig = (process) => {
        const messages = this._processMessageMap.get(process.longRunningProcessTypeId)(process);

        return {
            identifier: `LRP_${process.longRunningProcessId}`,
            longRunningProcessId: process.longRunningProcessId,
            title: process.isCompleted ? messages.complete : messages.title,
            progressActions: [ProgressActions.Cancel],
            isComplete: process.isCompleted,
            progressFailure: !!process.error,
            process: process,
            error: this._commonMethods.error,
            cancel: this._commonMethods.cancel,
            complete: (snack) => this._commonMethods.complete(snack, messages.complete),
            cancelled: this._commonMethods.cancelled(messages.cancelled),
        };
    };

    /**
     * Custom configurations
     * For unique cases a custom configuration can be provided
     */

    private _returnPrepareDataConfig: BaseConfig = (process) => ({
        ...this._baseConfig(process),
        isHidden: true,
    });

    private _finalizeTransmittalConfig: BaseConfig = (process) => {
        if (process.operationName.toLowerCase() === 'simulate') {
            return this._baseDownloadConfig(process);
        } else {
            return this._baseNavigateConfig(process);
        }
    };

    private _importFileConfig: BaseConfig = (process) => {
        if (process.operationName.includes('Validate') || process.operationName.includes('Transfer') || process.operationName.includes('Recalculate')) {
            const messages = this._processMessageMap.get(process.longRunningProcessTypeId)(process);
            return {
                identifier: `LRP_${process.longRunningProcessId}`,
                longRunningProcessId: process.longRunningProcessId,
                title: process.isCompleted ? messages.complete : messages.title,
                progressActions: [],
                isComplete: process.isCompleted,
                progressFailure: !!process.error,
                isHidden: true,
                process: process,
                error: this._commonMethods.error,
                complete: (snack) => this._commonMethods.complete(snack, messages.complete),
            };
        } else {
            return this._baseNavigateCancelConfig(process);
        }
    };

    private _returnReportDownloadConfig: BaseConfig = (process) => {
        return {
            identifier: `LRP_${process.longRunningProcessId}`,
            longRunningProcessId: process.longRunningProcessId,
            title: 'Return Report Download',
            progressActions: [ProgressActions.DownloadWithCancel],
            isComplete: process.isCompleted,
            progressFailure: !!process.error,
            error: this._commonMethods.error,
            cancel: this._commonMethods.cancel,
            cancelled: this._commonMethods.cancelled('Return report download cancelled'),
            process,
            download: async (snack) => {
                const response = await lastValueFrom(
                    this._returnReportRepository.getDownloadReport(process.longRunningProcessId),
                );
                if (response.status === 204) {
                    // A duplicate download (file already acknowledged) is distinguished from a legitimately empty
                    // report via this header. See work item 599886.
                    const isDuplicateDownload = response.headers.get('X-Duplicate-Download') === 'true';
                    const msg = isDuplicateDownload
                        ? 'This report has already been downloaded'
                        : 'The report contains no data';
                    snack.updateTitle(msg);
                    snack.setError(msg);
                    await lastValueFrom(of(null).pipe(delay(2000)));
                    return;
                }
                snack.updateTitle('Downloaded');
                await this._downloadFileService.downloadResponse(response);
            },
        };
    };

    private _attachmentBundlerDownloadConfig: BaseConfig = (process) => {
        return {
            identifier: `LRP_${process.longRunningProcessId}`,
            longRunningProcessId: process.longRunningProcessId,
            title: 'Bundling attachments',
            progressActions: [ProgressActions.DownloadWithCancel],
            isComplete: process.isCompleted,
            progressFailure: !!process.error,
            error: this._commonMethods.error,
            cancel: this._commonMethods.cancel,
            cancelled: this._commonMethods.cancelled('Bundling cancelled'),
            process,
            download: async (snack) => {
                if (!snack.process?.result) {
                    console.error('Process result contained no data.');
                }
                const parsedResult: Compliance.AttachmentBundlerGenerateFileResultModel = JSON.parse(
                    snack.process.result,
                );
                const fileIds = parsedResult.fileIds;

                for (const fileId of fileIds) {
                    const response = await lastValueFrom(this._attachmentBundlerRepository.downloadBundle(fileId));
                    await this._downloadFileService.downloadResponse(response);
                }
            },
        };
    };
}
